import { animated, useSpring } from '@react-spring/web';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { CaretDown, CaretUp } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getImageUrl, GetImageUrlParams } from '@/utils/urls';
import { InvoiceItem } from '@/types/Invoice';
import { ResizeObserver } from '@juggle/resize-observer';
import InvoiceSummaryList from './components/InvoiceSummaryList';
import InvoiceSummaryListRow from './components/InvoiceSummaryListRow';
import React, { useState } from 'react';
import styled from 'styled-components';
import useMeasure from 'react-use-measure';

type Props = {
    className?: string;
    currency: string;
    items: InvoiceItem[];
};

const InvoiceSummaryItems = ({ className, currency, items }: Props) => {
    const [collapsed, setCollapsed] = useState<boolean>(false);

    const [contentContainerRef, { height }] = useMeasure({
        polyfill: ResizeObserver,
    });
    const slideInStyles = useSpring({
        height: collapsed ? 0 : height,
        marginTop: collapsed ? 0 : '16px',
        opacity: collapsed ? 0 : 1,
    });

    return (
        <InvoiceSummaryList className={className}>
            <StyledListTitle onClick={() => setCollapsed(!collapsed)}>
                <FormattedMessage
                    id="checkout.invoice_summary.itemsInOrder"
                    values={{ count: items?.length }}
                />
                {collapsed ? (
                    <CaretDown
                        className="inline-flex"
                        color="primary"
                        size="sm"
                        style="fill"
                    />
                ) : (
                    <CaretUp
                        className="inline-flex"
                        color="primary"
                        size="sm"
                        style="fill"
                    />
                )}
            </StyledListTitle>
            <StyledItems style={slideInStyles}>
                <div ref={contentContainerRef}>
                    {items?.map((item) => {
                        const amount = parseFloat(item.salePrice);
                        if (isNaN(amount)) {
                            return null;
                        }
                        const imageParams: GetImageUrlParams = {
                            catalogId: Number(item.catalogId),
                            height: 40,
                            imageNumber: 1,
                            imageVersion: Number(item.imageVersion),
                            itemId: Number(item.itemId),
                            sellerId: Number(item.sellerId),
                        };
                        return (
                            <div key={item.itemId}>
                                <InvoiceSummaryListRow
                                    amount={{ amount: amount, currency }}
                                    label={
                                        <StyledSummaryItem>
                                            <StyledImageWrapper>
                                                <StyledImage
                                                    alt={item.title}
                                                    src={getImageUrl(imageParams)}
                                                />
                                            </StyledImageWrapper>
                                            <StyledTitle>{item.title}</StyledTitle>
                                        </StyledSummaryItem>
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
            </StyledItems>
        </InvoiceSummaryList>
    );
};

export default InvoiceSummaryItems;

const StyledListTitle = styled(BodySecondary)`
    display: flex;
    justify-content: space-between;
    cursor: pointer;
`;

const StyledItems = styled(animated.div)`
    margin: 16px 0;
    overflow: hidden;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-bottom: 0;
        flex-direction: column;
    }
`;

const StyledSummaryItem = styled.div`
    display: grid;
    grid-template-rows: auto;
    grid-template-columns:
        [image] 40px
        [spacer] 8px
        [title] auto;
    max-width: 90%;
`;

const StyledImageWrapper = styled.div`
    grid-area: 1 / image / 1 / spacer;
    background-color: ${({ theme }) => theme.colors.white};
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledImage = styled.img`
    max-height: 40px;
    max-width: 40px;
`;

const StyledTitle = styled(BodySecondary)`
    grid-area: 1 / title / 1 / -1;
    /* stylelint-disable value-no-vendor-prefix  */
    /* stylelint-disable property-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;
